import { useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ChevronRight,
  Gavel,
  History,
  Lock,
  MessageSquareWarning,
  Plus,
  Settings2,
  type LucideIcon,
} from 'lucide-react';
import { Badge, Button, Card, Select, Spinner } from '@/components/ui';
import type { HrCaseKind, HrCaseStatus } from '@/api/services/hrCases.service';
import { listHrCases } from '@/api/services/hrWorkspace.service';
import { usePermissions } from '@/hooks/usePermissions';
import { useMe } from '@/features/settings/hooks/useMe';
import type { MeRoleId } from '@/types/api.types';
import { cn } from '@/lib/cn';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { CaseWorkspaceDialog } from './CaseWorkspaceDialog';
import { OpenDisciplineDialog } from './OpenDisciplineDialog';
import { OrganizationAccessLogDialog } from './OrganizationAccessLogDialog';
import type { HrAreaProps } from '../hrShared';
import {
  CASE_STATUS,
  DECISION_LABELS,
  FINDING_LABELS,
  formatCaseDate,
  ROLE_LABELS,
  TERMINAL_STATUSES,
} from './caseFormat';

const PAGE_SIZE = 25;

const STATUS_FILTERS: Record<HrCaseKind, HrCaseStatus[]> = {
  discipline: ['open', 'investigating', 'hearing_scheduled', 'decided', 'closed', 'withdrawn'],
  grievance: [
    'blocked',
    'submitted',
    'acknowledged',
    'investigating',
    'findings_issued',
    'appeal_requested',
    'closed',
    'withdrawn',
  ],
};

/** What each kind of case is for, and the stages it moves through. */
const KINDS: Record<
  HrCaseKind,
  {
    label: string;
    icon: LucideIcon;
    purpose: string;
    processTitle: string;
    steps: Array<{ title: string; detail: string }>;
  }
> = {
  discipline: {
    label: 'Discipline',
    icon: Gavel,
    purpose: 'HR has a concern about an employee’s conduct or performance.',
    processTitle: 'How a disciplinary case runs',
    steps: [
      { title: 'Open', detail: 'HR records the concern and picks the case team.' },
      { title: 'Investigate', detail: 'Notes, evidence and statements go on the record.' },
      { title: 'Hearing', detail: 'The employee gets notice and a chance to respond.' },
      { title: 'Decision', detail: 'No action, a warning, or dismissal, with reasons.' },
      { title: 'Appeal', detail: 'The employee can appeal within the appeal window.' },
    ],
  },
  grievance: {
    label: 'Grievances',
    icon: MessageSquareWarning,
    purpose: 'An employee raises a concern about their work, a colleague, or how they are treated.',
    processTitle: 'How a grievance runs',
    steps: [
      { title: 'Raised', detail: 'The employee raises it from My HR Cases.' },
      { title: 'Acknowledge', detail: 'A handler confirms receipt within the set number of days.' },
      {
        title: 'Investigate',
        detail: 'The handler looks into it, with an investigator if needed.',
      },
      { title: 'Findings', detail: 'Upheld, partly upheld or not upheld, with any actions.' },
      { title: 'Appeal', detail: 'The employee can appeal the findings.' },
    ],
  },
};

function useOrganizationWide(): boolean {
  const { data: me } = useMe();
  return useMemo(() => {
    if (!me?.roleId || typeof me.roleId === 'string') return false;
    return (me.roleId as MeRoleId).scope === 'all';
  }, [me]);
}

function initialsOf(name: string | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return (
    (parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '')
  ).toUpperCase();
}

/** The stages of the selected kind, plus who can see the cases and where the rules live. */
function CaseProcess({
  kind,
  organizationWide,
}: {
  kind: HrCaseKind;
  organizationWide: boolean;
}): React.ReactElement {
  const [searchParams] = useSearchParams();
  const settings = new URLSearchParams();
  const company = searchParams.get('companyId');
  if (company) settings.set('companyId', company);
  settings.set('tab', 'setup');
  settings.set('section', 'cases');
  const { processTitle, steps } = KINDS[kind];

  return (
    <section
      aria-labelledby="hr-case-process"
      className="rounded-lg border border-gray-200 bg-gray-50/70 px-4 py-4 sm:px-5 dark:border-gray-700 dark:bg-gray-800/40"
    >
      <h3 id="hr-case-process" className="text-sm font-semibold text-gray-900 dark:text-gray-100">
        {processTitle}
      </h3>
      <ol className="mt-3 grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-5">
        {steps.map((step, index) => (
          <li key={step.title} className="relative flex gap-3 lg:block">
            <div className="flex items-center gap-2 lg:mb-2">
              <span
                aria-hidden
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-white text-xs font-semibold tabular-nums text-primary dark:border-sky-400/40 dark:bg-gray-900 dark:text-sky-300"
              >
                {index + 1}
              </span>
              {index < steps.length - 1 && (
                <span
                  aria-hidden
                  className="hidden h-px flex-1 bg-gray-200 lg:block dark:bg-gray-700"
                />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{step.title}</p>
              <p className="mt-0.5 text-xs leading-5 text-gray-600 dark:text-gray-300">
                {step.detail}
              </p>
            </div>
          </li>
        ))}
      </ol>
      <div className="mt-4 flex flex-col gap-2 border-t border-gray-200 pt-3 text-xs text-gray-600 sm:flex-row sm:items-center sm:justify-between dark:border-gray-700 dark:text-gray-300">
        <p className="flex items-start gap-1.5">
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            <span>
              {organizationWide
                ? 'You see every case as an owner or admin. Each case you open is logged.'
                : 'You see only the cases whose team you are on.'}
            </span>{' '}
            Notes marked Team only stay hidden from the people involved.
          </span>
        </p>
        <Link
          to={{ search: settings.toString() }}
          className="inline-flex shrink-0 items-center gap-1 font-medium text-primary hover:underline focus-visible:rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 dark:text-sky-300"
        >
          <Settings2 className="h-3.5 w-3.5" aria-hidden />
          Time limits and options
        </Link>
      </div>
    </section>
  );
}

/**
 * People & HR → Cases. Lists only the cases the viewer is entitled to: their
 * own case teams, or every case for owners and admins. Nothing here counts or
 * hints at cases outside that set.
 */
export function HrCasesTab({ companyIds, showCompany }: HrAreaProps): React.ReactElement {
  const { can } = usePermissions();
  const canDiscipline = can('hr', 'discipline');
  const canGrievances = can('hr', 'grievances');
  const organizationWide = useOrganizationWide();
  const [searchParams] = useSearchParams();
  const [kind, setKind] = useState<HrCaseKind>(() => {
    const requested = searchParams.get('kind');
    if (requested === 'grievance' && canGrievances) return 'grievance';
    if (requested === 'discipline' && canDiscipline) return 'discipline';
    return canDiscipline ? 'discipline' : 'grievance';
  });
  const [status, setStatus] = useState<HrCaseStatus | ''>('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<{ id: string; companyId: string } | null>(null);
  const [isOpening, setIsOpening] = useState(false);
  const [isAccessLogOpen, setIsAccessLogOpen] = useState(false);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['hr-cases', companyIds?.join(',') ?? '', kind, status, page],
    queryFn: () =>
      listHrCases(companyIds, { kind, status: status || undefined, page, limit: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  const rows = Array.isArray(data?.data) ? data.data : [];
  const total = typeof data?.total === 'number' ? data.total : rows.length;
  const hasMore = page * PAGE_SIZE < total;
  const allowedKinds = (['discipline', 'grievance'] as const).filter((value) =>
    value === 'discipline' ? canDiscipline : canGrievances
  );
  const isGrievance = kind === 'grievance';
  const EmptyIcon = KINDS[kind].icon;

  const pickKind = (value: HrCaseKind): void => {
    setKind(value);
    setStatus('');
    setPage(1);
  };
  const moveTab = (event: React.KeyboardEvent, index: number): void => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    const next = (index + step + allowedKinds.length) % allowedKinds.length;
    pickKind(allowedKinds[next]);
    tabRefs.current[next]?.focus();
  };

  return (
    <div className="space-y-4">
      {allowedKinds.length > 1 && (
        <div role="tablist" aria-label="Case kind" className="grid gap-2 sm:grid-cols-2">
          {allowedKinds.map((value, index) => {
            const { label, icon: Icon, purpose } = KINDS[value];
            const active = kind === value;
            return (
              <button
                key={value}
                ref={(node) => {
                  tabRefs.current[index] = node;
                }}
                type="button"
                role="tab"
                aria-selected={active}
                aria-controls="hr-cases-panel"
                tabIndex={active ? 0 : -1}
                onClick={() => pickKind(value)}
                onKeyDown={(event) => moveTab(event, index)}
                className={cn(
                  'flex min-w-0 items-start gap-3 rounded-lg border px-4 py-3.5 text-left',
                  'transition-[background-color,border-color] duration-150 motion-reduce:transition-none',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900',
                  active
                    ? 'border-primary/40 bg-primary/5 dark:border-primary/50 dark:bg-primary/10'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600 dark:hover:bg-gray-700/50'
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                    active
                      ? 'bg-primary text-white dark:bg-sky-500/20 dark:text-sky-200'
                      : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300'
                  )}
                >
                  <Icon className="h-4.5 w-4.5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      'block text-sm font-semibold',
                      active ? 'text-primary dark:text-sky-200' : 'text-gray-900 dark:text-gray-100'
                    )}
                  >
                    {label}
                  </span>
                  <span className="mt-0.5 block text-xs leading-5 text-gray-600 dark:text-gray-300">
                    {purpose}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div
        id="hr-cases-panel"
        role={allowedKinds.length > 1 ? 'tabpanel' : undefined}
        className="space-y-4"
      >
        <CaseProcess kind={kind} organizationWide={organizationWide} />

        <Card contentClassName="p-0">
          <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-3 lg:flex-row lg:items-center lg:justify-between dark:border-gray-700">
            <h3 className="flex items-baseline gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
              {isGrievance ? 'Grievances' : 'Disciplinary cases'}
              {!isLoading && !error && (
                <span className="text-xs font-normal tabular-nums text-gray-500 dark:text-gray-400">
                  {total}
                </span>
              )}
            </h3>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Select
                id="hr-case-status"
                label="Status"
                className="sm:w-48 [&>label]:sr-only"
                triggerClassName="sm:h-9 sm:min-h-9 sm:rounded-lg"
                value={status}
                onChange={(value) => {
                  setStatus(value as HrCaseStatus | '');
                  setPage(1);
                }}
                placeholder="All statuses"
                options={STATUS_FILTERS[kind].map((value) => ({
                  value,
                  label: CASE_STATUS[value].label,
                }))}
              />
              {(organizationWide || (!isGrievance && canDiscipline)) && (
                <div className="flex gap-2">
                  {organizationWide && (
                    <Button size="sm" variant="secondary" onClick={() => setIsAccessLogOpen(true)}>
                      <History className="mr-2 h-4 w-4" aria-hidden />
                      Access log
                    </Button>
                  )}
                  {!isGrievance && canDiscipline && (
                    <Button size="sm" onClick={() => setIsOpening(true)}>
                      <Plus className="mr-2 h-4 w-4" aria-hidden />
                      Open case
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div aria-live="polite" aria-busy={isFetching}>
            {isLoading ? (
              <div className="flex justify-center py-12" role="status">
                <Spinner />
                <span className="sr-only">Loading cases</span>
              </div>
            ) : error ? (
              <div className="space-y-3 px-4 py-6" role="alert">
                <p className="text-sm text-danger">{getErrorMessage(error)}</p>
                <Button variant="secondary" size="sm" onClick={() => void refetch()}>
                  Retry
                </Button>
              </div>
            ) : rows.length === 0 ? (
              <div className="flex flex-col items-center px-6 py-12 text-center">
                <span
                  aria-hidden
                  className="flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300"
                >
                  <EmptyIcon className="h-5 w-5" />
                </span>
                <p className="mt-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                  {status
                    ? 'No case you can see has this status.'
                    : isGrievance
                      ? organizationWide
                        ? 'No grievance has been raised.'
                        : 'You are not handling any grievance.'
                      : organizationWide
                        ? 'No disciplinary case has been opened.'
                        : 'You are not on any disciplinary case team.'}
                </p>
                <p className="mt-1 max-w-md text-xs leading-5 text-gray-600 dark:text-gray-300">
                  {status
                    ? 'Pick another status, or All statuses.'
                    : isGrievance
                      ? 'Employees raise grievances from My HR Cases. Each one appears here for its handler and case team.'
                      : 'A case appears here once HR opens one, for the people on its team.'}
                </p>
              </div>
            ) : (
              <>
                <div className="relative overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-gray-50 text-xs font-medium text-gray-500 dark:bg-gray-800/60 dark:text-gray-400">
                      <tr>
                        <th scope="col" className="px-4 py-2.5 font-medium">
                          {isGrievance ? 'Raised by' : 'Employee'}
                        </th>
                        {showCompany && (
                          <th scope="col" className="hidden px-4 py-2.5 font-medium lg:table-cell">
                            Company
                          </th>
                        )}
                        <th scope="col" className="hidden px-4 py-2.5 font-medium md:table-cell">
                          {isGrievance ? 'Category' : 'Type'}
                        </th>
                        <th scope="col" className="px-4 py-2.5 font-medium">
                          Status
                        </th>
                        <th scope="col" className="hidden px-4 py-2.5 font-medium xl:table-cell">
                          {isGrievance ? 'Raised' : 'Hearing'}
                        </th>
                        <th scope="col" className="hidden px-4 py-2.5 font-medium xl:table-cell">
                          Outcome
                        </th>
                        <th scope="col" className="hidden px-4 py-2.5 font-medium lg:table-cell">
                          Your role
                        </th>
                        <th scope="col" className="px-4 py-2.5">
                          <span className="sr-only">Open</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700/70">
                      {rows.map((row) => {
                        const person = isGrievance ? row.complainant?.name : row.subject?.name;
                        const outcome = row.decision
                          ? DECISION_LABELS[row.decision]
                          : row.finding
                            ? FINDING_LABELS[row.finding]
                            : null;
                        const open = (): void =>
                          setSelected({ id: row._id, companyId: row.companyId });
                        return (
                          <tr
                            key={row._id}
                            onClick={open}
                            className="group cursor-pointer transition-colors hover:bg-gray-50 motion-reduce:transition-none dark:hover:bg-gray-700/40"
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <span
                                  aria-hidden
                                  className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary sm:flex dark:bg-sky-400/15 dark:text-sky-200"
                                >
                                  {initialsOf(person)}
                                </span>
                                <div className="min-w-0">
                                  <p className="truncate font-medium text-gray-900 dark:text-gray-100">
                                    {person ?? '—'}
                                  </p>
                                  {isGrievance && row.subject && (
                                    <p className="text-xs text-gray-600 dark:text-gray-300">
                                      About {row.subject.name}
                                    </p>
                                  )}
                                  <p className="font-mono text-xs text-gray-500 dark:text-gray-400">
                                    {row.reference}
                                  </p>
                                </div>
                              </div>
                            </td>
                            {showCompany && (
                              <td className="hidden px-4 py-3 text-gray-700 dark:text-gray-200 lg:table-cell">
                                {row.companyName ?? '—'}
                              </td>
                            )}
                            <td className="hidden px-4 py-3 text-gray-700 dark:text-gray-200 md:table-cell">
                              {row.typeLabel}
                            </td>
                            <td className="px-4 py-3">
                              <Badge
                                variant={CASE_STATUS[row.status]?.variant ?? 'neutral'}
                                size="sm"
                              >
                                {CASE_STATUS[row.status]?.label ?? row.status}
                              </Badge>
                            </td>
                            <td className="hidden whitespace-nowrap px-4 py-3 tabular-nums text-gray-700 dark:text-gray-200 xl:table-cell">
                              {formatCaseDate(isGrievance ? row.openedAt : row.hearingAt)}
                            </td>
                            <td className="hidden px-4 py-3 xl:table-cell">
                              {outcome ? (
                                <span className="text-gray-700 dark:text-gray-200">{outcome}</span>
                              ) : TERMINAL_STATUSES.includes(row.status) ? (
                                <span className="text-gray-500 dark:text-gray-400">—</span>
                              ) : (
                                <span className="text-gray-500 dark:text-gray-400">Pending</span>
                              )}
                            </td>
                            <td className="hidden px-4 py-3 text-gray-700 dark:text-gray-200 lg:table-cell">
                              {row.myRoles.length
                                ? row.myRoles.map((role) => ROLE_LABELS[role]).join(', ')
                                : 'Oversight'}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                aria-label={`Open case ${row.reference}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  open();
                                }}
                              >
                                Open
                                <ChevronRight
                                  className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none"
                                  aria-hidden
                                />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-4 py-3 dark:border-gray-700">
                  <span className="text-xs tabular-nums text-gray-600 dark:text-gray-300">
                    {total > PAGE_SIZE
                      ? `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total} cases`
                      : `${total} case${total === 1 ? '' : 's'}`}
                  </span>
                  {total > PAGE_SIZE && (
                    <div className="flex gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={page === 1 || isFetching}
                        onClick={() => setPage((value) => Math.max(1, value - 1))}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={!hasMore || isFetching}
                        onClick={() => setPage((value) => value + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </Card>
      </div>

      {selected && (
        <CaseWorkspaceDialog
          key={selected.id}
          companyId={selected.companyId}
          caseId={selected.id}
          organizationWide={organizationWide}
          onClose={() => setSelected(null)}
        />
      )}
      {isOpening && (
        <OpenDisciplineDialog
          companyIds={companyIds}
          showCompany={showCompany}
          onOpened={(opened) => {
            setIsOpening(false);
            setSelected(opened);
          }}
          onClose={() => setIsOpening(false)}
        />
      )}
      {isAccessLogOpen && <OrganizationAccessLogDialog onClose={() => setIsAccessLogOpen(false)} />}
    </div>
  );
}
